import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { IonRouterLink } from '@ionic/react';
import equal from 'deep-equal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import ArticlesTable from '@components/ArticlesTable';
import ActionIconButton from '@components/buttons/ActionIconButton';
import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import ContentCondition from '@components/ContentCondition';
import AsyncSelect, { Option } from '@components/fields/AsyncSelect';
import BaseErrors from '@components/fields/BaseErrors';
import CheckboxGroup from '@components/fields/CheckboxGroup';
import DataSelect from '@components/fields/DataSelect';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import RichTextArea from '@components/fields/RichTextArea';
import Select from '@components/fields/Select';
import TagSelect from '@components/fields/TagSelect';
import ContentConditionForm, { FormData as ContentConditionFormData } from '@components/forms/ContentConditionForm';
import Form from '@components/forms/Form';
import styles from '@components/forms/GoalTemplateForm.module.css';
import LargeLabel from '@components/labels/LargeLabel';
import Modal from '@components/Modal';
import Icon from '@components/text/Icon';
import TextWithIcon from '@components/text/TextWithIcon';
import useAsyncSearch from '@hooks/useAsyncSearch';
import useLazyQueryWithPromise from '@hooks/useLazyQueryWithPromise';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useOpener from '@hooks/useOpener';
import {
  ArticleContentTypeEnum,
  CarePlanActionItemCtaEnum,
  CarePlanTaskTemplate,
  CarePlanTaskTemplateSortByEnum,
  ConditionalContentTypeEnum,
  ContentConditionCreateDocument,
  ContentConditionCreateMutation,
  ContentConditionCreateMutationVariables,
  ContentConditionDestroyDocument,
  ContentConditionDestroyMutation,
  ContentConditionDestroyMutationVariables,
  ContentConditionKindEnum,
  FocusAreaEnum,
  GetCarePlanTaskTemplatesBasicDocument,
  GetCarePlanTaskTemplatesBasicQuery,
  GetCarePlanTaskTemplatesBasicQueryVariables,
  GetGoalTemplateQuery,
  GetWorksheetTemplatesBasicDocument,
  GetWorksheetTemplatesBasicQuery,
  GetWorksheetTemplatesBasicQueryVariables,
  GoalTemplateFragment as GoalTemplate,
  GoalTemplateCreateDocument,
  GoalTemplateCreateMutation,
  GoalTemplateCreateMutationVariables,
  GoalTemplateUpdateDocument,
  GoalTemplateUpdateMutation,
  GoalTemplateUpdateMutationVariables,
  TagTypeEnum,
  UserTypeEnum,
  WorksheetTemplate,
  WorksheetTemplateSortByEnum
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { clickCurrentTarget } from '@utils/keyboardUtils';
import { sortByStringField, sortByTitle } from '@utils/modelUtils';
import { optionsForFocusArea } from '@utils/optionUtils';

enum EditingKinds {
  CONTENT = 'content',
  EXPERT_TASKS = 'expert_tasks',
  MEMBER_TASKS = 'member_tasks',
  TOOLS = 'tools',
  TRIGGERS = 'triggers'
}

enum ToolKinds {
  FORMS = 'forms',
  TRACKER = 'tracker',
  WORKSHEETS = 'worksheets'
}

type TemplateValue = {
  id: string;
  title: string;
  userType: UserTypeEnum;
};

type TitleObject = {
  id: string;
  title: string;
};

type ContentCondition = NonNullable<NonNullable<GetGoalTemplateQuery['goalTemplate']['contentConditions']>[number]>;

type FormData = {
  articles: TitleObject[];
  carePlanTaskTemplates: TemplateValue[];
  contentConditions: ContentCondition[];
  contentConditionsNew: ContentConditionFormData[];
  countryCodes: string[];
  currentTool?: string;
  description?: string | null;
  draftValue?: string;
  focusAreas: FocusAreaEnum[];
  tags: string[];
  title: string;
  toolKind?: ToolKinds;
  tools: CarePlanActionItemCtaEnum[];
  worksheets: TitleObject[];
};

type Props = {
  autofocus: boolean;
  goalTemplate?: GoalTemplate;
  journeyId?: string;
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const GoalTemplateForm = ({ autofocus, goalTemplate, journeyId, onCancel, onError, onSuccess }: Props) => {
  const modalOpener = useOpener();
  const [editingKind, setEditingKind] = useState<EditingKinds | null>(null);

  const initialValues: FormData = {
    articles: goalTemplate?.articles.length ? goalTemplate.articles : [],
    carePlanTaskTemplates: goalTemplate?.carePlanTaskTemplates.length ? goalTemplate.carePlanTaskTemplates : [],
    contentConditions: goalTemplate?.contentConditions.length ? goalTemplate.contentConditions : [],
    contentConditionsNew: [],
    countryCodes: goalTemplate?.countryCodes.length ? goalTemplate.countryCodes : goalTemplate ? ['GLOBAL'] : [],
    description: goalTemplate?.description ?? '',
    focusAreas: goalTemplate?.focusAreas ?? [],
    tags: goalTemplate?.tags ?? [],
    title: goalTemplate?.title ?? '',
    tools: goalTemplate?.tools ?? [],
    worksheets: goalTemplate?.worksheetTemplates.length ? goalTemplate.worksheetTemplates : []
  };

  const intl = useIntl();
  const [draftTaskTemplates, setDraftTaskTemplates] = useState<Option[]>([]);

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createGoalTemplate, { client }] = useMutation<GoalTemplateCreateMutation, GoalTemplateCreateMutationVariables>(
    GoalTemplateCreateDocument,
    {
      update: () => {
        client.resetStore();
      }
    }
  );
  const [updateGoalTemplate] = useMutation<GoalTemplateUpdateMutation, GoalTemplateUpdateMutationVariables>(
    GoalTemplateUpdateDocument
  );

  const getCarePlanTaskTemplates = useLazyQueryWithPromise<
    GetCarePlanTaskTemplatesBasicQuery,
    GetCarePlanTaskTemplatesBasicQueryVariables
  >(GetCarePlanTaskTemplatesBasicDocument);

  const [createContentCondition] = useMutation<ContentConditionCreateMutation, ContentConditionCreateMutationVariables>(
    ContentConditionCreateDocument
  );

  const [destroyContentCondition] = useMutationWithValidation<
    ContentConditionDestroyMutation,
    ContentConditionDestroyMutationVariables
  >(ContentConditionDestroyDocument, 'contentCondition');

  const getWorksheetTemplates = useLazyQueryWithPromise<
    GetWorksheetTemplatesBasicQuery,
    GetWorksheetTemplatesBasicQueryVariables
  >(GetWorksheetTemplatesBasicDocument);

  const schema = yup
    .object()
    .shape({
      articles: yup.array().required(intl.formatMessage({ id: 'errors.goalTemplate.articleIds.blank' })),
      carePlanTaskTemplates: yup
        .array()
        .required(intl.formatMessage({ id: 'errors.goalTemplate.carePlanTaskTemplateIds.blank' })),
      countryCodes: yup.array().min(1, intl.formatMessage({ id: 'errors.goalTemplate.countryCodes.blank' })),
      description: yup.string().nullable(),
      focusAreas: yup.array().min(1, intl.formatMessage({ id: 'errors.goalTemplate.focusAreas.blank' })),
      tags: yup.array().of(yup.string()),
      title: yup.string().required(intl.formatMessage({ id: 'errors.goalTemplate.title.blank' })),
      tools: yup.array().required(intl.formatMessage({ id: 'errors.goalTemplate.tools.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const articles = form.watch('articles');
  const title = form.watch('title');
  const description = form.watch('description');
  const carePlanTaskTemplates = form.watch('carePlanTaskTemplates');
  const worksheets = form.watch('worksheets');
  const countryCodes = form.watch('countryCodes');
  const tools = form.watch('tools');
  const toolKind = form.watch('toolKind');
  const currentTool = form.watch('currentTool');
  const contentConditions = form.watch('contentConditions');
  const contentConditionsNew = form.watch('contentConditionsNew');

  useEffect(() => {
    form.setValue('draftValue', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolKind]);

  const onTemplateAdd = (options: Option[]) => {
    const newValues = options
      .map(option => ({
        id: option.value,
        title: option.label,
        userType: editingKind === EditingKinds.EXPERT_TASKS ? UserTypeEnum.expert : UserTypeEnum.client
      }))
      .filter(value => !carePlanTaskTemplates.some(template => template.id === value.id));
    form.setValue('carePlanTaskTemplates', [...carePlanTaskTemplates, ...newValues]);
  };

  const onItemAdd = (option: Option, worksheet = false) => {
    if (editingKind === EditingKinds.CONTENT) {
      if (!articles.some(t => t.id === option.value)) {
        form.setValue('articles', [
          ...articles,
          {
            id: option.value,
            title: option.label
          }
        ]);
      }
    } else if (editingKind === EditingKinds.TOOLS) {
      if (worksheet) {
        if (!worksheets.some(c => c.id === option.value)) {
          form.setValue('worksheets', [
            ...worksheets,
            {
              id: option.value,
              title: option.label
            }
          ]);
        }
      } else {
        if (!tools.some(t => t === option.value)) {
          form.setValue('tools', [...tools, option.value as CarePlanActionItemCtaEnum]);
        }
      }
    }
  };

  const templateDestroy = (value: string) => {
    form.setValue(
      'carePlanTaskTemplates',
      carePlanTaskTemplates.filter(template => template.id !== value)
    );
  };

  const conditionDestroy = (value?: string, contentCondition?: ContentConditionFormData | ContentCondition) => {
    if (value) {
      form.setValue('contentConditions', [...contentConditions.filter(condition => condition.id !== value)]);
    }

    form.setValue('contentConditionsNew', [
      ...contentConditionsNew.filter(condition => !equal(condition, contentCondition))
    ]);
  };

  const handleConditionAdd = (condition?: ContentConditionFormData) => {
    if (condition && !contentConditionsNew.some(c => equal(c, condition))) {
      form.setValue('contentConditionsNew', [...contentConditionsNew, condition]);
    }
    modalOpener.close();
  };

  const articleDestroy = (value: string) => {
    form.setValue(
      'articles',
      articles.filter(article => article.id !== value)
    );
  };

  const worksheetDestroy = (value: string) => {
    form.setValue(
      'worksheets',
      worksheets.filter(article => article.id !== value)
    );
  };

  const toolDestroy = (value: string) => {
    form.setValue(
      'tools',
      tools.filter(tool => tool !== value)
    );
  };

  const toolKindOptions = Object.values(ToolKinds).map(option => ({
    label: intl.formatMessage({ id: `models.goalTemplate.toolKind.${option}` }),
    value: option as string
  }));

  useEffect(() => {
    if (countryCodes.length > 1 && countryCodes.includes('GLOBAL')) {
      form.setError('countryCodes', {
        message: intl.formatMessage({ id: 'errors.goalTemplate.countryCodes.globalError' })
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCodes]);

  const formOptions = [CarePlanActionItemCtaEnum.complete_consent_form].map(option => ({
    label: intl.formatMessage({ id: `models.goalTemplate.tools.${option}` }),
    value: option as string
  }));

  const trackerOptions = [
    CarePlanActionItemCtaEnum.complete_legal_document_tracker,
    CarePlanActionItemCtaEnum.complete_medication_tracker,
    CarePlanActionItemCtaEnum.complete_provider_list
  ].map(option => ({
    label: intl.formatMessage({ id: `models.goalTemplate.tools.${option}` }),
    value: option as string
  }));

  const currentToolOptions = useMemo(() => {
    if (toolKind === ToolKinds.TRACKER) return trackerOptions;
    return formOptions;
  }, [formOptions, toolKind, trackerOptions]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    if (goalTemplate?.id) {
      await Promise.all(
        contentConditionsNew.map(condition =>
          createContentCondition({
            variables: {
              ...condition,
              contentId: goalTemplate.id,
              contentType: ConditionalContentTypeEnum.goal_template,
              kind: condition.kind as ContentConditionKindEnum
            }
          })
        )
      );

      await Promise.all(
        goalTemplate.contentConditions
          .filter(condition => !contentConditions.some(cc => cc.id === condition.id))
          .map(cc =>
            destroyContentCondition({
              id: cc.id
            })
          )
      );

      return updateGoalTemplate({
        variables: {
          ...form.getValues(),
          articleIds: form.getValues().articles.map(a => a.id),
          carePlanTaskTemplateIds: form.getValues().carePlanTaskTemplates.map(c => c.id),
          countryCodes: form.getValues().countryCodes.filter(country => country !== 'GLOBAL'),
          id: goalTemplate.id,
          worksheetTemplateIds: form.getValues().worksheets.map(c => c.id)
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.goalTemplateUpdate,
          intl,
          onError,
          onSuccess: () => {
            if (onSuccess) {
              onSuccess();
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.goalTemplate'
        });
      });
    }
    return createGoalTemplate({
      variables: {
        ...form.getValues(),
        articleIds: form.getValues().articles.map(a => a.id),
        carePlanTaskTemplateIds: form.getValues().carePlanTaskTemplates.map(c => c.id),
        countryCodes: form.getValues().countryCodes.filter(country => country !== 'GLOBAL'),
        journeyId,
        worksheetTemplateIds: form.getValues().worksheets.map(c => c.id)
      }
    }).then(async response => {
      if (response.data?.goalTemplateCreate?.goalTemplate?.id) {
        await Promise.all(
          contentConditionsNew.map(condition =>
            createContentCondition({
              variables: {
                ...condition,
                contentId: response.data?.goalTemplateCreate?.goalTemplate?.id as string,
                contentType: ConditionalContentTypeEnum.goal_template,
                kind: condition.kind as ContentConditionKindEnum
              }
            })
          )
        );
      }
      handleMutationResponse<FormData>({
        data: response.data?.goalTemplateCreate,
        intl,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.goalTemplate'
      });
    });
  }, [
    goalTemplate?.id,
    goalTemplate?.contentConditions,
    createGoalTemplate,
    form,
    journeyId,
    contentConditionsNew,
    updateGoalTemplate,
    createContentCondition,
    contentConditions,
    destroyContentCondition,
    intl,
    onError,
    onSuccess
  ]);

  const focusAreaOptions = optionsForFocusArea(intl, false);

  const templateAsyncSearch = useAsyncSearch<CarePlanTaskTemplate>({
    defaultOptions: [],
    getLabel: t => t?.title ?? '',
    initialOption: { label: '', value: null }
  });

  const worksheetAsyncSearch = useAsyncSearch<WorksheetTemplate>({
    defaultOptions: [],
    getLabel: t => t?.title ?? '',
    initialOption: { label: '', value: null }
  });

  const aiInputs = useMemo(() => {
    const options = [
      { description: 'title', value: title },
      { description: 'description', value: description ?? '' }
    ];

    if (articles.length > 0) {
      options.push({
        description: 'articles',
        value: articles.map(article => article.title).join(', ')
      });
    }
    if (worksheets.length > 0) {
      options.push({
        description: 'worksheets',
        value: worksheets.map(worksheet => worksheet.title).join(', ')
      });
    }
    if (carePlanTaskTemplates.length > 0) {
      options.push({
        description: 'tasks',
        value: carePlanTaskTemplates.map(worksheet => worksheet.title).join(', ')
      });
    }
    if (tools.length > 0) {
      options.push({
        description: 'tools',
        value: tools.join(', ')
      });
    }
    return options;
  }, [articles, carePlanTaskTemplates, description, title, tools, worksheets]);

  return (
    <>
      <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
        <BaseErrors errors={baseErrors} />
        <Input<FormData>
          autofocus={autofocus}
          control={form.control}
          error={form.formState.errors.title}
          label={intl.formatMessage({ id: 'forms.goalTemplate.titleLabel' })}
          name="title"
          required
        />
        <RichTextArea<FormData>
          control={form.control}
          error={form.formState.errors.description}
          journeyId={null}
          label={intl.formatMessage({ id: 'forms.goalTemplate.descriptionLabel' })}
          name="description"
          required
          sharableURLSWithoutJourneyId
        />
        <DataSelect<FormData>
          control={form.control}
          error={form.formState.errors.countryCodes}
          global
          isMulti
          label={intl.formatMessage({ id: 'forms.article.countriesLabel' })}
          name="countryCodes"
          required
          subLabel={intl.formatMessage({ id: 'forms.article.countriesSubLabel' })}
          type="countries"
        />
        <div className={styles.topSpacing}>
          <CheckboxGroup<FormData>
            control={form.control}
            errors={form.formState.errors.focusAreas}
            label={intl.formatMessage({ id: 'forms.goalTemplate.focusAreasLabel' })}
            name="focusAreas"
            options={focusAreaOptions}
            required
          />
        </div>
        <div className={styles.topSpacing}>
          <LargeLabel>
            <FormattedMessage id="forms.goalTemplate.triggers" />
          </LargeLabel>
        </div>
        {contentConditions.map(condition => (
          <ContentCondition contentCondition={condition} key={condition.id} onDelete={conditionDestroy} />
        ))}
        {contentConditionsNew.map(condition => (
          <ContentCondition contentCondition={condition} key={Math.random()} onDelete={conditionDestroy} />
        ))}
        <IonRouterLink
          onClick={() => {
            setEditingKind(EditingKinds.TRIGGERS);
            modalOpener.open();
          }}
        >
          <TextWithIcon icon="add" iconFirst text={intl.formatMessage({ id: 'forms.goalTemplate.addTrigger' })} />
        </IonRouterLink>

        <div className={styles.topSpacing}>
          <LargeLabel>
            <FormattedMessage id="forms.goalTemplate.memberTask" />
          </LargeLabel>
        </div>

        {sortByTitle(carePlanTaskTemplates)
          .filter(template => template.userType === UserTypeEnum.client)
          .map(template => (
            <div className={styles.template} key={template.id}>
              <div>{template.title}</div>
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.delete"
                icon="trash"
                onClick={() => templateDestroy(template.id)}
              />
            </div>
          ))}
        <div>
          <IonRouterLink
            onClick={() => {
              setEditingKind(EditingKinds.MEMBER_TASKS);
              modalOpener.open();
            }}
          >
            <TextWithIcon icon="add" iconFirst text={intl.formatMessage({ id: 'forms.goalTemplate.addTasks' })} />
          </IonRouterLink>
        </div>
        <div className={styles.topSpacing}>
          <LargeLabel>
            <FormattedMessage id="forms.goalTemplate.expertTasks" />
          </LargeLabel>
        </div>
        {sortByTitle(carePlanTaskTemplates)
          .filter(template => template.userType === UserTypeEnum.expert)
          .map(template => (
            <div className={styles.template} key={template.id}>
              <div>{template.title}</div>
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.delete"
                icon="trash"
                onClick={() => templateDestroy(template.id)}
              />
            </div>
          ))}
        <div>
          <IonRouterLink
            onClick={() => {
              setEditingKind(EditingKinds.EXPERT_TASKS);
              modalOpener.open();
            }}
          >
            <TextWithIcon icon="add" iconFirst text={intl.formatMessage({ id: 'forms.goalTemplate.addTasks' })} />
          </IonRouterLink>
        </div>
        <div className={styles.topSpacing}>
          <LargeLabel>
            <FormattedMessage id="forms.goalTemplate.content" />
          </LargeLabel>
        </div>
        {sortByTitle(articles).map(article => (
          <div className={styles.article} key={article.id}>
            <IonRouterLink onClick={() => articleDestroy(article.id)}>
              <Icon ariaLabelKey="accessibility.ariaLabels.delete" icon="trash" />
            </IonRouterLink>
            <div>{article.title}</div>
          </div>
        ))}
        <div>
          <IonRouterLink
            onClick={() => {
              setEditingKind(EditingKinds.CONTENT);
              modalOpener.open();
            }}
          >
            <TextWithIcon icon="add" iconFirst text={intl.formatMessage({ id: 'forms.goalTemplate.addContent' })} />
          </IonRouterLink>
        </div>

        <div className={styles.topSpacing}>
          <LargeLabel>
            <FormattedMessage id="forms.goalTemplate.tools" />
          </LargeLabel>
        </div>
        {tools.sort().map(tool => (
          <div className={styles.article} key={tool}>
            <IonRouterLink onClick={() => toolDestroy(tool)}>
              <Icon ariaLabelKey="accessibility.ariaLabels.delete" icon="trash" />
            </IonRouterLink>
            <div>
              <FormattedMessage id={`models.goalTemplate.tools.${tool}`} />
            </div>
          </div>
        ))}
        {sortByTitle(worksheets).map(worksheet => (
          <div className={styles.article} key={worksheet.id}>
            <IonRouterLink onClick={() => worksheetDestroy(worksheet.id)}>
              <Icon ariaLabelKey="accessibility.ariaLabels.delete" icon="trash" />
            </IonRouterLink>
            <div>{worksheet.title}</div>
          </div>
        ))}
        <div>
          <IonRouterLink
            onClick={() => {
              setEditingKind(EditingKinds.TOOLS);
              modalOpener.open();
            }}
          >
            <TextWithIcon icon="add" iconFirst text={intl.formatMessage({ id: 'forms.goalTemplate.addTool' })} />
          </IonRouterLink>
        </div>
        <TagSelect<FormData>
          aiInputs={aiInputs}
          control={form.control}
          errors={form.formState.errors.tags}
          label={intl.formatMessage({ id: 'forms.carePlanTaskTemplate.tagsLabel' })}
          name="tags"
          tagType={TagTypeEnum.GOAL_TEMPLATE}
        />
        <FormButtons
          hookForm={form}
          onCancel={onCancel}
          saveKey={goalTemplate?.id ? 'dictionary.saveChanges' : 'models.goalTemplate.create'}
        />
      </Form>

      <Modal
        cssClass="big"
        onCancel={() => {
          form.setValue('draftValue', '');
          form.setValue('toolKind', undefined);
          form.setValue('currentTool', undefined);
        }}
        opener={modalOpener}
        title={
          editingKind
            ? intl.formatMessage({
                id: `forms.goalTemplate.add${editingKind}`
              })
            : ''
        }
      >
        {() => {
          if (editingKind === EditingKinds.MEMBER_TASKS || editingKind === EditingKinds.EXPERT_TASKS) {
            return (
              <>
                <AsyncSelect<FormData>
                  control={form.control}
                  defaultOptions={templateAsyncSearch.defaultOptions}
                  error={form.formState.errors.carePlanTaskTemplates?.[0] as FieldError | undefined}
                  label={intl.formatMessage({ id: 'forms.goalTemplate.actionItemLibrary' })}
                  name="draftValue"
                  onSearch={async searchTerm =>
                    templateAsyncSearch.searchOptions(
                      searchTerm,
                      getCarePlanTaskTemplates({
                        variables: {
                          count: 10,
                          searchTerm,
                          sortBy: CarePlanTaskTemplateSortByEnum.title,
                          userType:
                            editingKind === EditingKinds.MEMBER_TASKS ? UserTypeEnum.client : UserTypeEnum.expert
                        }
                      })
                    )
                  }
                  onSelection={option => {
                    if (!draftTaskTemplates.some(template => template.value === option.value)) {
                      setDraftTaskTemplates([...draftTaskTemplates, option]);
                    }
                  }}
                  value={{ label: '', value: '' }}
                />
                <div className={styles.topSpacing}>
                  {sortByStringField(draftTaskTemplates, template => template.value).map(template => (
                    <div className={styles.template} key={template.value}>
                      <div>{template.label}</div>
                      <ActionIconButton
                        ariaLabelKey="accessibility.ariaLabels.delete"
                        icon="trash"
                        onClick={() =>
                          setDraftTaskTemplates(draftTaskTemplates.filter(tt => tt.value !== template.value))
                        }
                      />
                    </div>
                  ))}
                </div>
                <div className={styles.topSpacing}>
                  <SecondaryButton
                    onClick={() => {
                      setDraftTaskTemplates([]);
                      modalOpener.close();
                    }}
                    onKeyUp={clickCurrentTarget}
                    tabIndex={0}
                    textKey="dictionary.cancel"
                  />
                  <PrimaryButton
                    data-test="submit-button"
                    disabled={!draftTaskTemplates.length}
                    onClick={() => {
                      onTemplateAdd(draftTaskTemplates);
                      setDraftTaskTemplates([]);
                      modalOpener.close();
                    }}
                    onKeyUp={clickCurrentTarget}
                    tabIndex={0}
                    textKey="forms.goalTemplate.addTasks"
                    type="submit"
                  />
                </div>
              </>
            );
          } else if (editingKind === EditingKinds.CONTENT) {
            return (
              <ArticlesTable
                contentTypes={[ArticleContentTypeEnum.text, ArticleContentTypeEnum.video]}
                includeHidden={false}
                journeyId={null}
                onSelect={(article: TitleObject) => {
                  onItemAdd({ label: article.title, value: article.id });
                  form.setValue('draftValue', '');
                }}
                selectedArticleIds={articles.map(article => article.id)}
                skipScroll
              />
            );
          } else if (editingKind === EditingKinds.TOOLS) {
            return (
              <>
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.toolKind}
                  label={intl.formatMessage({ id: 'forms.goalTemplate.toolKindLabel' })}
                  name="toolKind"
                  options={toolKindOptions}
                />
                <div className={toolKind === ToolKinds.TRACKER || toolKind === ToolKinds.FORMS ? '' : 'ion-hide'}>
                  <Select<FormData>
                    control={form.control}
                    error={form.formState.errors.currentTool}
                    label={intl.formatMessage({ id: 'forms.goalTemplate.selectTracker' })}
                    name="currentTool"
                    options={currentToolOptions}
                  />
                </div>
                <div className={toolKind === ToolKinds.WORKSHEETS ? '' : 'ion-hide'}>
                  <AsyncSelect<FormData>
                    control={form.control}
                    defaultOptions={worksheetAsyncSearch.defaultOptions}
                    error={form.formState.errors.worksheets?.[0] as FieldError | undefined}
                    label={intl.formatMessage({ id: 'forms.goalTemplate.worksheetLibrary' })}
                    name="draftValue"
                    onSearch={async searchTerm =>
                      worksheetAsyncSearch.searchOptions(
                        searchTerm,
                        getWorksheetTemplates({
                          variables: {
                            count: 10,
                            searchTerm,
                            sortBy: WorksheetTemplateSortByEnum.title
                          }
                        })
                      )
                    }
                    onSelection={option => worksheetAsyncSearch.setCurrentOption(option)}
                    value={worksheetAsyncSearch.currentOption}
                  />
                </div>
                <div className={styles.topSpacing}>
                  <SecondaryButton
                    onClick={() => {
                      worksheetAsyncSearch.setCurrentOption(null);
                      modalOpener.close();
                    }}
                    onKeyUp={clickCurrentTarget}
                    tabIndex={0}
                    textKey="dictionary.cancel"
                  />
                  <PrimaryButton
                    data-test="submit-button"
                    disabled={!currentTool && !worksheetAsyncSearch.currentOption?.value}
                    onClick={() => {
                      if (toolKind === ToolKinds.WORKSHEETS) {
                        if (worksheetAsyncSearch.currentOption) {
                          onItemAdd(worksheetAsyncSearch.currentOption, true);
                        }
                      } else {
                        const optionValue = trackerOptions
                          .concat(formOptions)
                          .find(option => option.value === currentTool);
                        if (optionValue) {
                          onItemAdd(optionValue);
                        }
                      }
                      worksheetAsyncSearch.setCurrentOption(null);
                      modalOpener.close();
                    }}
                    onKeyUp={clickCurrentTarget}
                    tabIndex={0}
                    textKey="dictionary.add"
                    type="submit"
                  />
                </div>
              </>
            );
          } else if (editingKind === EditingKinds.TRIGGERS) {
            return <ContentConditionForm onCancel={modalOpener.close} onSuccess={handleConditionAdd} />;
          }
        }}
      </Modal>
    </>
  );
};

export default GoalTemplateForm;
